import { computed, reactive } from 'vue'
import { cloudApi, CloudApiError, currentWorkspaceType, hasWebSessionHint } from '@/api/index.js'

const state = reactive({
  user: null,
  tenants: [],
  isSystemAdmin: false,
  availableWorkspaces: [],
  selectedTenant: null,
  assignment: null,
  sessionChecked: false,
  busy: false,
})

const isAuthenticated = computed(() => Boolean(state.user))
let sessionRequest = null
let authChannel = null
const workspaceType = currentWorkspaceType()

function sessionFingerprint(session = {}) {
  const tenants = (Array.isArray(session.tenants) ? session.tenants : [])
    .map((tenant) => `${tenant.id}:${tenant.role_code || ''}`)
    .sort()
  return JSON.stringify({
    userId: session.user?.id || null,
    isSystemAdmin: Boolean(session.is_system_admin ?? session.isSystemAdmin),
    tenants,
  })
}

function currentSessionFingerprint() {
  return sessionFingerprint({
    user: state.user,
    tenants: state.tenants,
    isSystemAdmin: state.isSystemAdmin,
  })
}

function applySession(session) {
  const previousFingerprint = currentSessionFingerprint()
  const nextFingerprint = sessionFingerprint(session)
  const changed = state.sessionChecked && previousFingerprint !== nextFingerprint
  const selectedTenantId = state.selectedTenant?.id
  state.user = session.user
  state.tenants = Array.isArray(session.tenants) ? session.tenants : []
  state.isSystemAdmin = Boolean(session.is_system_admin)
  state.availableWorkspaces = Array.isArray(session.available_workspaces) ? session.available_workspaces : []
  if (changed) {
    state.selectedTenant = null
    state.assignment = null
  } else if (selectedTenantId) {
    state.selectedTenant = state.tenants.find((tenant) => tenant.id === selectedTenantId) || null
    if (!state.selectedTenant) state.assignment = null
  }
  state.sessionChecked = true
  return changed
}

function clearSession() {
  const changed = state.sessionChecked && Boolean(state.user || state.isSystemAdmin || state.tenants.length)
  state.user = null
  state.tenants = []
  state.isSystemAdmin = false
  state.availableWorkspaces = []
  state.selectedTenant = null
  state.assignment = null
  state.sessionChecked = true
  return changed
}

async function revalidateSession() {
  // Session and refresh cookies are HTTP-only. Each workspace has its own
  // browser-readable CSRF cookie, which is also the hint that its session may
  // exist. Anonymous visitors should see login without producing a noisy 401.
  if (!hasWebSessionHint(workspaceType)) {
    const changed = clearSession()
    return { authenticated: false, changed }
  }
  if (!sessionRequest) {
    sessionRequest = cloudApi.get('/api/web/auth/session', { refreshOnUnauthorized: true })
      .then((session) => {
        const changed = applySession(session)
        return { authenticated: true, changed }
      })
      .catch((error) => {
        if (error instanceof CloudApiError && error.status === 401) {
          const changed = clearSession()
          return { authenticated: false, changed }
        }
        throw error
      })
      .finally(() => {
        sessionRequest = null
      })
  }
  return sessionRequest
}

async function ensureSession() {
  if (state.sessionChecked) return isAuthenticated.value
  return (await revalidateSession()).authenticated
}

function notifySessionChanged() {
  authChannel?.postMessage({ type: 'session-changed' })
}

async function login(email, password, requestedWorkspaceType = workspaceType) {
  state.busy = true
  try {
    const session = await cloudApi.post(
      '/api/web/auth/login',
      { email, password, workspace_type: requestedWorkspaceType },
      { refreshOnUnauthorized: false, workspaceType: requestedWorkspaceType },
    )
    applySession(session)
    state.selectedTenant = null
    state.assignment = null
    const target = await prepareDefaultTenant()
    notifySessionChanged()
    return target
  } finally {
    state.busy = false
  }
}

async function logout() {
  state.busy = true
  try {
    await cloudApi.post('/api/web/auth/logout', null, { refreshOnUnauthorized: false })
  } finally {
    clearSession()
    notifySessionChanged()
    state.busy = false
  }
}

async function selectTenant(tenantId) {
  const tenant = state.tenants.find((item) => item.id === tenantId)
  if (!tenant) throw new CloudApiError('该企业空间不在当前账号权限范围内。', { code: 'TENANT_NOT_ALLOWED' })
  state.selectedTenant = tenant
  state.assignment = null
  if (['owner', 'admin'].includes(tenant.role_code)) return '/enterprise-admin/dashboard'
  if (!['member', 'project_manager'].includes(tenant.role_code)) return '/assignment-review'
  const currentProject = await cloudApi.get(
    `/api/tenants/${encodeURIComponent(tenant.id)}/account-current-project`,
  )
  state.assignment = {
    ...currentProject,
    state: currentProject.state === 'CURRENT' ? 'assigned' : String(currentProject.state || '').toLowerCase(),
    project: currentProject.current_project || null,
  }
  return routeForCurrentState()
}

async function refreshSelectedTenantWorkspace() {
  if (!state.selectedTenant) return prepareDefaultTenant()
  return selectTenant(state.selectedTenant.id)
}

async function prepareDefaultTenant() {
  if (!state.user) return '/login'
  if (state.isSystemAdmin) return '/superadmin/dashboard'
  if (state.selectedTenant) return routeForCurrentState()
  if (state.tenants.length === 1) return selectTenant(state.tenants[0].id)
  return '/select-enterprise'
}

function routeForCurrentState() {
  if (!state.user) return '/login'
  if (state.isSystemAdmin) return '/superadmin/dashboard'
  if (!state.selectedTenant) return '/select-enterprise'
  if (['owner', 'admin'].includes(state.selectedTenant.role_code)) return '/enterprise-admin/dashboard'
  if (state.assignment?.state === 'assigned') {
    return `/project-manager/projects/${encodeURIComponent(state.assignment.project.id)}/dashboard`
  }
  if (state.assignment?.state === 'unassigned') return '/waiting-for-project'
  return '/assignment-review'
}

if (typeof window !== 'undefined') {
  window.addEventListener('pm:session-expired', clearSession)
  if (typeof BroadcastChannel !== 'undefined') {
    authChannel = new BroadcastChannel(`ai-marketing-paas-auth:${workspaceType}`)
    authChannel.addEventListener('message', (event) => {
      if (event.data?.type === 'session-changed') {
        window.dispatchEvent(new CustomEvent('pm:session-replaced'))
      }
    })
  }
}

export const authStore = Object.freeze({
  state,
  workspaceType,
  isAuthenticated,
  ensureSession,
  revalidateSession,
  login,
  logout,
  selectTenant,
  refreshSelectedTenantWorkspace,
  prepareDefaultTenant,
  routeForCurrentState,
})
